/**
 * RegistrationsTab — Manage → Registrations. Organizer sees all incoming
 * public registrations for this auction and can Approve (→ creates a Player
 * row) or Reject each pending entry.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageInput";
import { CheckCircle, XCircle, Copy, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RegistrationsTab({ auction }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/auctions/${auction.id}/registrations`, { params: filter === "all" ? {} : { status: filter } });
      setRows(data);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [auction.id, filter]);

  useEffect(() => { load(); }, [load]);

  const publicLink = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/register/${auction.slug || auction.id}`;
  }, [auction.id, auction.slug]);

  const approve = async (r) => {
    try {
      await api.post(`/auctions/${auction.id}/registrations/${r.id}/approve`);
      toast.success(`${r.player_name} approved & added to players`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approval failed"); }
  };
  const reject = async (r) => {
    try {
      await api.post(`/auctions/${auction.id}/registrations/${r.id}/reject`, { reason: "" });
      toast(`${r.player_name} rejected`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reject failed"); }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success("Public link copied to clipboard");
    } catch (e) {
      // Clipboard blocked (insecure context / permission denied) — offer a manual fallback.
      toast.error("Copy blocked — long-press the URL to copy manually");
    }
  };
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get(`/auctions/${auction.id}/registrations/export`, { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registrations_${auction.slug || auction.id}${filter === "all" ? "" : "_" + filter}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Exported ${filter === "all" ? "all" : filter} registrations`);
    } catch (e) {
      toast.error("Export failed — please retry");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="registrations-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mono">PUBLIC REGISTRATION LINK</div>
          <div className="flex items-center gap-2 mt-1">
            <code className="bg-black/60 border border-zinc-800 rounded px-2 py-1 text-xs text-emerald-300 max-w-[440px] truncate" data-testid="registration-public-link">{publicLink}</code>
            <button type="button" onClick={copyLink} className="btn-ghost text-xs flex items-center gap-1" data-testid="copy-register-link">
              <Copy size={12} /> Copy
            </button>
          </div>
        </div>
        <div className="flex gap-1 items-center flex-wrap">
          {[
            { k: "pending", l: "Pending" },
            { k: "approved", l: "Approved" },
            { k: "rejected", l: "Rejected" },
            { k: "all", l: "All" },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setFilter(f.k)}
              className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded border ${filter === f.k ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-800 text-zinc-400 hover:text-white"}`}
              data-testid={`reg-filter-${f.k}`}
            >{f.l}</button>
          ))}
          <button
            type="button"
            onClick={exportXlsx}
            disabled={exporting}
            className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded border border-yellow-500/70 bg-yellow-400 hover:bg-yellow-300 text-slate-900 flex items-center gap-1 disabled:opacity-60"
            title="Download every registration in this view as Excel — includes optional & custom fields, UTR and screenshot link"
            data-testid="export-registrations-xlsx"
          >
            <DownloadSimple weight="bold" size={12} /> {exporting ? "Exporting…" : "Export XLSX"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="label-mono py-16 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded p-12 text-center text-zinc-500">
          No {filter === "all" ? "" : filter} registrations yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((r) => (
            <div key={r.id} className="bg-zinc-900 border border-zinc-800 rounded p-3 flex gap-3" data-testid={`registration-row-${r.id}`}>
              {r.photo_url ? (
                <img src={resolveImageUrl(r.photo_url)} alt="" className="w-24 h-28 rounded object-cover border border-zinc-800 flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="w-24 h-28 rounded border border-dashed border-zinc-800 grid place-items-center text-zinc-600 text-xs flex-shrink-0">No photo</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display uppercase text-lg leading-tight truncate">{r.player_name}</div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${r.status === "approved" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : r.status === "rejected" ? "bg-red-500/20 text-red-300 border border-red-500/40" : "bg-amber-500/20 text-amber-300 border border-amber-500/40"}`}>{r.status}</span>
                </div>
                <div className="label-mono truncate">{r.mobile} · {r.role} · {r.batting_style || "—"}</div>
                <div className="text-xs text-zinc-400 mt-1 line-clamp-2">
                  {[r.age && `${r.age} yrs`, r.location, r.village, r.district].filter(Boolean).join(" · ")}
                </div>
                <div className="text-xs text-emerald-400 mt-1">Base {r.base_price || auction.settings?.default_base_price} · Cat {r.category}</div>
                {r.status === "pending" && (
                  <div className="flex gap-1.5 mt-2">
                    <button type="button" onClick={() => approve(r)} className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1" data-testid={`approve-${r.id}`}>
                      <CheckCircle weight="fill" size={12} /> Approve
                    </button>
                    <button type="button" onClick={() => reject(r)} className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded bg-red-600 hover:bg-red-500 text-white flex items-center gap-1" data-testid={`reject-${r.id}`}>
                      <XCircle weight="fill" size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
